import { arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  clearCourseRouteGeometryUnlessManualPolyline,
  courseDraggingStationsTempIdsAtom,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  createErrorResult,
  errors,
  hasRoutingWaypointAtRouteEnds,
  ICourseStationInfo,
  inheritLegMetricsForManualCourseChange,
  inheritPredecessorTimeForStation,
  Log,
  RebuildRouteTypeEnum,
  reorderCourseStationsByOrderedArray,
  SimpleResult,
  TDragDropCourseSortable,
  useSystemParams,
  useTimezone,
} from "@ryde/shared";

import { useCourseDtoActions } from "./use-course-dto-actions";

export const useDragCourseStationsReorder = () => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { doNotAutoUpdateCourses } = useSystemParams();
  const { updateEditingCourseMetadata, applyEditingCourseMetadataLocally } =
    useCourseDtoActions();
  const setTempDragStationsIds = useSetRecoilState(
    courseDraggingStationsTempIdsAtom,
  );
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const dragReorderStationsCourse = useEvent(
    async (
      source: TDragDropCourseSortable,
      destination: TDragDropCourseSortable,
    ): Promise<SimpleResult<void>> => {
      Log.debug(
        "Drag reorder stations payloads",
        source?.payload,
        destination?.payload,
      );
      const courseId = source?.payload?.courseDto?.id || 0;
      const courseToId = destination?.payload?.courseDto?.id || 0;

      if (courseId !== courseToId) {
        return new SimpleResult({
          success: false,
          error: t("errors.cantReorderStationOfDifferentCourses", {
            courseId1: courseId || "-",
            courseId2: courseToId || "-",
          }),
        });
      }

      const courseDto = source?.payload?.courseDto;

      if (!courseDto) {
        return createErrorResult(
          "errors.cantReorderStationInvalidDragCourseObject",
        );
      }

      const stationsOldIds = [...source.sortable.items];
      const oldIndex = source.sortable.index;
      const newIndex = destination.sortable.index;

      if (oldIndex === newIndex) {
        Log.warn(t(errors.ride.sameIndexReturnedNoNeedToReorder));
        return new SimpleResult({
          success: false,
          error: t(errors.ride.sameIndexReturnedNoNeedToReorder),
        });
      }

      const stationsUpdatedIds = arrayMove(stationsOldIds, oldIndex, newIndex);
      const reorderedStations = stationsUpdatedIds
        .map(stationId =>
          courseDto.metadata?.stationsInfo?.find(
            station => station.id === stationId,
          ),
        )
        .filter((station): station is ICourseStationInfo => Boolean(station));

      if (hasRoutingWaypointAtRouteEnds(reorderedStations)) {
        return createErrorResult(
          errors.ride.routingWaypointCannotBeRouteEndpoint,
        );
      }

      setTempDragStationsIds(stationsUpdatedIds);
      Log.debug(
        `Stations order before and after sorting for course id ${courseId}:`,
        stationsOldIds,
        stationsUpdatedIds,
      );

      if (!source?.payload?.courseDto) {
        return createErrorResult(errors.course.invalidCourseDragDataMismatch);
      }

      if ((courseDto.id || 0) !== courseId) {
        return createErrorResult(errors.course.invalidCourseDragIdMismatch);
      }

      const metadataRecounted =
        reorderCourseStationsByOrderedArray(
          courseDto.metadata,
          stationsUpdatedIds,
          timezone,
        ) || {};

      // Manual ordering: give the dragged station its predecessor's time so the backend's stable
      // time-sort keeps it in the dropped position instead of snapping it back by its old time.
      const stationsWithInheritedTime = inheritPredecessorTimeForStation({
        stationsInfo: metadataRecounted.stationsInfo || [],
        stationId: stationsUpdatedIds[newIndex],
      });

      const hasManualPolyline = Boolean(
        courseDto.metadata?.[CourseMetadataKeys.isManualPolylineEdit],
      );

      const metadataToSave =
        doNotAutoUpdateCourses || hasManualPolyline
          ? inheritLegMetricsForManualCourseChange({
              previousStationsInfo: courseDto.metadata?.stationsInfo || [],
              metadata: {
                ...metadataRecounted,
                stationsInfo: stationsWithInheritedTime,
              },
            })
          : {
              ...metadataRecounted,
              stationsInfo: stationsWithInheritedTime,
            };

      const metadataForUpdate = clearCourseRouteGeometryUnlessManualPolyline({
        metadata: metadataToSave,
        doNotAutoUpdateCourses,
      });

      Log.debug(
        "Composed metadata after station drag for checking dry save course:",
        metadataForUpdate,
      );

      // Mirror ride drag: push new visit order + drop stale geometry before recount so the map OSRM-previews immediately.
      applyEditingCourseMetadataLocally({
        metadata: metadataForUpdate,
        skipReorderStationsByTime: true,
      });
      setCourseHasUnsavedStationChange(true);

      const updateResult = await updateEditingCourseMetadata({
        metadata: metadataForUpdate,
        optimize: false,
        rebuildType: RebuildRouteTypeEnum.KeepOrder,
        skipReorderStationsByTime: true,
      });

      setTempDragStationsIds(undefined);

      return updateResult;
    },
  );
  return { dragReorderStationsCourse };
};
